import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Droplets } from "lucide-react";

const ChidiyaWaterfall = () => {
  const navigate = useNavigate();
  const waterfallImage = "/Indore/waterfalls/chidiya0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Chidiya Waterfall",
    "description": "Charming waterfall near Indore offering a peaceful retreat amidst nature",
    "image": waterfallImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Chidiya Waterfall Area",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 06:00-18:00",
    "priceRange": "₹10-50",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.2",
      "reviewCount": "3800"
    }
  };

  const highlights = [
    {
      icon: <Droplets className="w-6 h-6" />,
      title: "Peaceful Waterfall",
      description: "Charming waterfall with gentle cascades and serene surroundings"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Nature Photography",
      description: "Ideal spot for capturing beautiful natural landscapes"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Tranquil Retreat",
      description: "Perfect destination for relaxation and nature appreciation"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Natural Oasis",
      description: "Surrounded by lush greenery and natural beauty"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "₹10 for adults, ₹5 for children" },
    { label: "Timings", value: "6:00 AM - 6:00 PM (All days)" },
    { label: "Best Time", value: "Monsoon season (July-September)" },
    { label: "Duration", value: "1-2 hours" },
    { label: "Facilities", value: "Parking, Basic amenities" }
  ];

  const activities = [
    { name: "Nature Walk", description: "Stroll through the surrounding natural trails" },
    { name: "Photography", description: "Capture the serene beauty of the waterfall" },
    { name: "Relaxation", description: "Enjoy peaceful moments in nature" },
    { name: "Bird Watching", description: "Observe local bird species" }
  ];

  const nearbyAttractions = [
    { name: "Patalpani Waterfall", distance: "12 km", time: "20 mins" },
    { name: "Tincha Waterfall", distance: "18 km", time: "28 mins" },
    { name: "Bamniya Waterfall", distance: "8 km", time: "15 mins" },
    { name: "Choral Dam", distance: "22 km", time: "32 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Chidiya Waterfall Indore - Peaceful Natural Retreat | Aaoghumen</title>
        <meta name="description" content="Visit Chidiya Waterfall near Indore with Aaoghumen - charming waterfall with serene surroundings. Complete guide with timings and natural beauty." />
        <meta name="keywords" content="chidiya waterfall indore, chidiya waterfall timings, indore waterfalls, peaceful waterfall, aaoghumen chidiya, nature spots indore" />

        <meta property="og:title" content="Chidiya Waterfall Indore - Serene Beauty | Aaoghumen" />
        <meta property="og:description" content="Experience the peaceful beauty of Chidiya Waterfall near Indore, a perfect retreat for nature lovers." />
        <meta property="og:image" content={waterfallImage} />
        <meta name="twitter:image" content={waterfallImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/chidiya-waterfall" />

        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/chidiya-waterfall" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-emerald-600 via-green-500 to-teal-600 overflow-hidden">
          <img
            src={waterfallImage}
            alt="Chidiya Waterfall Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-emerald-300">Chidiya Waterfall</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Discover the peaceful charm of Chidiya Waterfall, a serene natural retreat near Indore.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.2 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>6 AM - 6 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>₹10 Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Chidiya Waterfall</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Chidiya Waterfall is a charming natural attraction located near Indore, offering visitors a peaceful retreat amidst lush greenery. The waterfall features gentle cascades that create a soothing atmosphere, making it perfect for those seeking tranquility away from city life.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The surrounding area is rich in natural beauty with abundant flora and fauna, providing excellent opportunities for nature lovers to connect with the environment. The waterfall is especially beautiful during the monsoon season when the water flow is abundant.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, explore the serene beauty of Chidiya Waterfall, engage in peaceful activities, and create lasting memories in this natural oasis.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Waterfall Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-emerald-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-emerald-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Activities */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Activities & Experiences</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {activities.map((activity, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-emerald-600 mb-2">{activity.name}</h3>
                  <p className="text-gray-600">{activity.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-emerald-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Find Peace in Nature</h2>
              <p className="text-xl mb-6">Visit Chidiya Waterfall and experience serene natural beauty</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Chidiya Waterfall Area, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-emerald-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Waterfall Visit
                </button>
                <button
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-emerald-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default ChidiyaWaterfall;